(() => {
  const key = '__spNewsletter';
  if (window[key]) { window[key].scan(document); return; }
  const instances = new Map();
  const shownScopes = new Set();
  const init = root => {
    if (instances.has(root)) return;
    const dialog = root.querySelector('dialog');
    const abort = new AbortController();
    let opener = null;
    let overflow = null;
    let destroyed = false;
    let timer = null;
    let elapsed = 0;
    let activeSince = document.hidden ? null : performance.now();
    const scope = root.dataset.scope || window.Shopify?.theme?.id;
    const storageKey = `spNewsletter:${scope}`;
    const numberSetting = (name, fallback, min, max) => {
      const raw = root.dataset[name];
      const value = raw == null || raw === '' ? fallback : Number(raw);
      return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
    };
    const delay = numberSetting('autoDelay', 30, 3, 600) * 1000;
    const scrollPercent = numberSetting('autoScroll', 30, 0, 100);
    const dismissDays = numberSetting('dismissDays', 30, 1, 365);
    const read = (store, suffix) => { try { return window[store].getItem(`${storageKey}:${suffix}`); } catch (_) { return null; } };
    const write = (store, suffix, value) => { try { window[store].setItem(`${storageKey}:${suffix}`, String(value)); } catch (_) { /* In-memory suppression still applies. */ } };
    const stopTimer = () => { if (timer !== null) clearTimeout(timer); timer = null; };
    const markShown = () => {
      shownScopes.add(storageKey);
      write('sessionStorage', 'shown', 1);
      stopTimer();
    };
    const dismiss = () => {
      markShown();
      write('localStorage', 'dismissedUntil', Date.now() + dismissDays * 86400000);
    };
    const suppressed = () => shownScopes.has(storageKey) || read('sessionStorage', 'shown') === '1' || read('localStorage', 'subscribed') === '1' || Number(read('localStorage', 'dismissedUntil')) > Date.now();
    const close = () => { if (dialog?.open) dialog.close(); };
    const restore = () => {
      if (dialog?.open) return;
      if (overflow !== null) { document.body.style.overflow = overflow; overflow = null; }
      if (opener?.isConnected) opener.focus();
      opener = null;
    };
    const open = target => {
      if (!dialog || typeof dialog.showModal !== 'function') return false;
      if (dialog.open) return true;
      opener = target || document.activeElement;
      try { dialog.showModal(); } catch (_) { opener = null; return false; }
      markShown();
      if (overflow === null) overflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-sp-newsletter-title]')?.focus();
      return true;
    };
    document.addEventListener('click', event => {
      const link = event.target.closest?.('[data-sp-newsletter-open],a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target.toLowerCase() !== '_self')) return;
      if (link.matches('a[href]')) {
        let url;
        try { url = new URL(link.href, window.location.href); } catch (_) { return; }
        if (url.origin !== window.location.origin || !/\/pages\/newsletter\/?$/.test(url.pathname) || url.search || url.hash) return;
      } else if (!link.matches('[data-sp-newsletter-open]')) return;
      if (open(link)) event.preventDefault();
    }, { signal: abort.signal });
    root.querySelectorAll('[data-sp-newsletter-close]').forEach(button => button.addEventListener('click', close, { signal: abort.signal }));
    if (dialog) {
      dialog.addEventListener('close', () => { if (!destroyed) dismiss(); restore(); }, { signal: abort.signal });
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')].filter(el => el.tabIndex >= 0 && !el.disabled && el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first) return;
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, { signal: abort.signal });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close();
      }, { signal: abort.signal });
      if (root.querySelector('[data-sp-newsletter-feedback]')) open(null);
    } else {
      root.querySelector('[data-sp-newsletter-feedback]')?.focus();
    }
    if (root.querySelector('[data-sp-newsletter-success], [data-sp-newsletter-feedback][role="status"]')) {
      markShown();
      write('localStorage', 'subscribed', 1);
    }
    const autoAllowed = dialog && scope && root.dataset.autoEnabled !== 'false' && root.dataset.designMode !== 'true' && !window.Shopify?.designMode && !/^customers\//.test(root.dataset.pageType || '') && !/(?:^|\/)(?:account|accounts|customer|customers|challenge|checkpoint)(?:\/|$)/.test(window.location.pathname) && !/\/pages\/newsletter\/?$/.test(window.location.pathname);
    const tryAuto = () => {
      stopTimer();
      if (destroyed || !autoAllowed || suppressed() || document.hidden) return;
      const activeTime = elapsed + (activeSince === null ? 0 : performance.now() - activeSince);
      if (activeTime < delay) { timer = setTimeout(tryAuto, delay - activeTime); return; }
      const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
      if (pageHeight <= 0 || window.scrollY / pageHeight * 100 < scrollPercent) return;
      if (document.querySelector('dialog[open], [aria-expanded="true"]') || document.activeElement?.closest('input,textarea,select,form,[contenteditable]:not([contenteditable="false"])')) return;
      open(null);
    };
    const visibility = () => {
      if (document.hidden) {
        if (activeSince !== null) elapsed += performance.now() - activeSince;
        activeSince = null;
        stopTimer();
      } else {
        if (activeSince === null) activeSince = performance.now();
        tryAuto();
      }
    };
    if (autoAllowed) {
      window.addEventListener('scroll', tryAuto, { passive: true, signal: abort.signal });
      document.addEventListener('visibilitychange', visibility, { signal: abort.signal });
      document.addEventListener('focusout', () => queueMicrotask(tryAuto), { signal: abort.signal });
      document.addEventListener('close', tryAuto, { capture: true, signal: abort.signal });
      document.addEventListener('click', () => queueMicrotask(tryAuto), { signal: abort.signal });
      document.addEventListener('keydown', () => queueMicrotask(tryAuto), { signal: abort.signal });
      tryAuto();
    }
    instances.set(root, () => { destroyed = true; stopTimer(); close(); restore(); abort.abort(); instances.delete(root); });
  };
  const scan = container => {
    instances.forEach((destroy, root) => { if (!root.isConnected) destroy(); });
    if (container.matches?.('[data-sp-newsletter]')) init(container);
    container.querySelectorAll('[data-sp-newsletter]').forEach(init);
  };
  window[key] = { scan };
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => instances.forEach((destroy, root) => { if (e.target === root || e.target.contains(root)) destroy(); }));
  scan(document);
})();

